import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { difference, sortBy } from 'es-toolkit';
import { Autocomplete, Button, DialogTitle, Divider, List, Stack, TextField, Typography } from '@mui/material';

import { groupMembersAtom, siteMembersAtom } from '@/interfaces/member';
import { selectedGroupIdAtom } from '@/interfaces/group';
import { permissionChangeAtom } from '@/interfaces/auth';
import ListItemWithDelete from '@/components/common/ListItemWithDelete';

import type { SyntheticEvent } from 'react';
import type { AutocompleteChangeReason } from '@mui/material';
import type { PermissionData } from '@/@types/user';

interface ManageMembersDialogProps {
  groupName: string;
  onClose: () => void;
}
interface AutocompleteItem {
  label: string;
  id: string;
}

export default function ManageMembersDialog({ groupName, onClose }: ManageMembersDialogProps) {
  const { data: siteMembers } = useAtomValue(siteMembersAtom);
  const { data: members } = useAtomValue(groupMembersAtom);
  const selectedGroupId = useAtomValue(selectedGroupIdAtom);
  const { mutate, isPending, data: isSuccess } = useAtomValue(permissionChangeAtom);

  const [autocompleteList, setAutocompleteList] = useState<AutocompleteItem[]>([]);
  const [selectedMember, setSelectedMember] = useState<AutocompleteItem>(null);
  const [inputValue, setInputValue] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<AutocompleteItem[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (siteMembers === undefined || members === undefined) return;

    const selectedMemberIds = members.map((member) => member.id);
    const selectedItems: AutocompleteItem[] = [];
    const autocompleteItems: AutocompleteItem[] = [];
    for (const member of siteMembers) {
      if (selectedMemberIds.includes(member.id)) {
        selectedItems.push({ label: `${member.info?.name} (${member.email})`, id: member.id });
      } else {
        autocompleteItems.push({ label: `${member.info?.name} (${member.email})`, id: member.id });
      }
    }
    setSelectedMembers(sortBy(selectedItems, ['label']));
    setAutocompleteList(sortBy(autocompleteItems, ['label']));
  }, [members, siteMembers]);
  useEffect(() => {
    if (isSuccess === undefined) return;
    if (isSuccess) {
      onClose();
    } else {
      window.alert(t('Settings.message.editFailed'));
    }
  }, [isSuccess, onClose, t]);

  function onAutocompleteChange(_: SyntheticEvent, item: AutocompleteItem, reason: AutocompleteChangeReason) {
    if (reason !== 'selectOption') return;
    setAutocompleteList(autocompleteList.filter((device) => device.id !== item.id));
    setSelectedMembers([...selectedMembers, item]);
    setSelectedMember(null);
    setInputValue('');
  }
  function onRemoveClick(item: AutocompleteItem) {
    setSelectedMembers(selectedMembers.filter((member) => member.id !== item.id));
    setAutocompleteList(sortBy([...autocompleteList, item], ['label']));
  }
  function onSaveClick() {
    const oldMemberIds = members.map((member) => member.id);
    const newMemberIds = selectedMembers.map((member) => member.id);
    const memberIdsToAdd = difference(newMemberIds, oldMemberIds);
    const memberIdsToRemove = difference(oldMemberIds, newMemberIds);

    for (const memberId of memberIdsToAdd) {
      const data: PermissionData = {
        userId: memberId,
        resourceType: 'groups',
        resourceId: selectedGroupId,
        permissions: ['read', 'write'],
      };
      mutate(data);
    }
    for (const memberId of memberIdsToRemove) {
      const data: PermissionData = {
        userId: memberId,
        resourceType: 'groups',
        resourceId: selectedGroupId,
        permissions: [],
      };
      mutate(data);
    }
  }

  return (
    <Stack gap={2} sx={{ minWidth: 480, p: 3 }}>
      <DialogTitle sx={{ px: 0 }}>{t('Settings.label.manageMembers')}</DialogTitle>
      <Typography>{t('Settings.label.groupName')}</Typography>
      <TextField disabled value={groupName} />
      <Typography>{t('Settings.label.selectMember')}</Typography>
      <Autocomplete
        autoHighlight
        options={autocompleteList}
        value={selectedMember}
        inputValue={inputValue}
        onChange={onAutocompleteChange}
        onInputChange={(_, value) => setInputValue(value)}
        renderInput={(params) => <TextField {...params} />}
      />
      <List>
        <Divider />
        {selectedMembers.map((member) => (
          <ListItemWithDelete key={member.id} label={member.label} onRemoveClick={() => onRemoveClick(member)} />
        ))}
      </List>

      <Stack direction="row" justifyContent="center" gap={3}>
        <Button variant="contained" onClick={onClose} sx={{ bgcolor: '#CCC', color: 'black' }}>
          {t('Common.label.cancel')}
        </Button>
        <Button variant="contained" disabled={isPending} onClick={onSaveClick} sx={{ bgcolor: 'info.main' }}>
          {t('Common.label.saveButton')}
        </Button>
      </Stack>
    </Stack>
  );
}
